import { inject, observer } from "mobx-react";
import { Component } from "react";
import { FormattedMessage, injectIntl, type IntlShape } from "react-intl";
import { compose } from "nvent-web/utils/compose";
import PageTitle from "nvent-web/App/components/PageTitle";
import AppStore from "nvent-web/stores/App";
import { PromotionsStore } from "nvent-web/stores/Promotions";
import { UserStore } from "nvent-web/stores/User";
import style from "./ProPageCommon.module.scss";
import { Banner } from "./components/Banner/Banner";
import { getLogoByCountryCode } from "./utils/getLogoByCountryCode";
import { Promotion } from "./components/Promotion/Promotion";
import { getEmailByCountryCode } from "./utils/getEmailByCountryCode";
import type { KnownLocale } from "~/nvent-web/i18n";
import { CertProBenefitsList } from "./components/CertProBenefitsList/CertProBenefitsList";
import { FormattedHTMLMessage } from "~/nvent-web/components/FormattedHTMLMessage/FormattedHTMLMessage";

interface CertifiedProPagePropsInner {
  intl: IntlShape;
  user: UserStore;
  app: AppStore;
  promotions: PromotionsStore;
}

class CertifiedProPageInner extends Component<CertifiedProPagePropsInner> {
  state = {
    logoUrl: "",
  };

  constructor(props: CertifiedProPagePropsInner) {
    super(props);
  }

  async setLogoUrl() {
    const logoUrl = await getLogoByCountryCode(
      this.props.user.locale as KnownLocale
    );

    if (this.state.logoUrl === logoUrl) {
      return;
    }

    this.setState({ logoUrl });
  }

  componentDidUpdate() {
    this.setLogoUrl();
  }

  componentDidMount() {
    this.setLogoUrl();
  }

  render() {
    const { user, promotions } = this.props;
    const contactEmail = getEmailByCountryCode(user.companyAddress.countryCode);

    const generalPromotions = promotions
      .promotionsByCountryCode(user.companyAddress.countryCode)
      .filter(
        (promotion) =>
          promotion.availability === "only_non_certified_pro" &&
          promotion.status === "active"
      );

    const certProPromotions = promotions.promotions.filter(
      (promotion) =>
        promotion.availability === "only_certified_pro" &&
        promotion.status === "active"
    );

    const websiteUrl =
      user.companyAddress.countryCode.toLowerCase() === "se"
        ? "https://raychemgolvvarme.se/HITTA-INSTALLATOR"
        : "https://www.chemelex.com/raychem/where-to-buy";

    return (
      <>
        <section className={style.header}>
          <PageTitle>
            <FormattedMessage id="certifiedPage.certified.title" />
          </PageTitle>
        </section>

        <section className={style.wrapper}>
          <div className={style.wrapperColumn}>
            <Banner>
              <span>
                <FormattedHTMLMessage id="certifiedPage.certified.banner" />
              </span>
            </Banner>
          </div>
          {!promotions.arePromotionsLoading && generalPromotions.length ? (
            <div className={style.wrapperColumn}>
              {generalPromotions.map((promotion) => (
                <Promotion promotion={promotion} key={promotion.id} />
              ))}
            </div>
          ) : null}
        </section>

        <section className={style.wrapper}>
          <CertProBenefitsList
            contactEmail={contactEmail}
            websiteUrl={websiteUrl}
            promotions={certProPromotions}
            logoUrl={this.state.logoUrl}
          />
        </section>
      </>
    );
  }
}

export const CertifiedProPage = compose(
  inject("user", "app", "promotions"),
  injectIntl,
  observer
)(CertifiedProPageInner);
